import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class DarkModeService {
  // to conditionally display different things based on dark mode status in different components
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  public readonly darkMode$ = this.darkModeSubject.asObservable(); // expose as public observable

  private themeModeSubject = new BehaviorSubject<ThemeMode>('system');

  initializeTheme() {
    // Retrieve the stored theme preference
    const storedTheme = localStorage.getItem('color-theme');
    let isDarkMode: boolean;
    let themeMode: ThemeMode;

    if (storedTheme === null) {
      // No stored preference, fallback to system preference
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      themeMode = 'system';
    } else if (storedTheme === 'tw-dark') {
      isDarkMode = true;
      themeMode = 'dark';
    } else {
      isDarkMode = false;
      themeMode = 'light';
    }

    // Apply the theme to the document
    if (isDarkMode) {
      document.documentElement.classList.add('tw-dark');
    } else {
      document.documentElement.classList.remove('tw-dark');
    }

    // Update the subjects
    this.darkModeSubject.next(isDarkMode);
    this.themeModeSubject.next(themeMode);

    // Update Intercom theme on initialization
    this.updateIntercomTheme(themeMode);
  }

  toggleTheme() {
    const isCurrentlyDark = document.documentElement.classList.toggle('tw-dark');
    this.setDarkMode(isCurrentlyDark);
  }

  setDarkMode(darkMode: boolean) {
    const themeMode: ThemeMode = darkMode ? 'dark' : 'light';

    if (darkMode) {
      localStorage.setItem('color-theme', 'tw-dark');
    } else {
      localStorage.setItem('color-theme', 'light');
    }

    this.darkModeSubject.next(darkMode);
    this.themeModeSubject.next(themeMode);

    // Update Intercom theme
    this.updateIntercomTheme(themeMode);
  }

  /**
   * Update Intercom theme to match app theme
   */
  private updateIntercomTheme(themeMode: ThemeMode): void {
    if ((window as any)['Intercom']) {
      (window as any)['Intercom']('update', {
        theme_mode: themeMode,
      });
    }
  }

  get currentDarkModeValue(): boolean {
    return this.darkModeSubject.getValue();
  }

  /**
   * Get the current theme mode as an observable
   */
  getThemeMode(): Observable<ThemeMode> {
    return this.themeModeSubject.asObservable();
  }

  /**
   * Get the current theme mode value
   */
  getThemeModeValue(): ThemeMode {
    return this.themeModeSubject.getValue();
  }
}
